import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Icon, Pill, SkeletonLoader } from './components';
import { Role, Asset, subjects, formatData, DatasetSubtypeOperation } from './helpers';
import { useDocumentTitle, useGlobalState } from './hooks';
import {
  Entity,
  Connection,
  Connections,
  CustomProperty,
  CustomProperties,
  Dataset,
  DatasetGroup,
  Datasource,
  Field,
  Attribute,
  AttributeValue,
  AttributeUnits,
  Glossaries,
  Glossary,
  Home,
  Job,
  Profile,
  Schedule,
  Schedules,
  Settings,
  System,
  Systems,
  Task,
  Tasks,
  User,
  Users,
  UserGroup,
  UserGroups,
} from './views';

const CurrentField = ({ match }) => {
  const res = subjects.currentField.value;

  const loading =
    res === 'not_found' || (res?.field_id && res.field_id.toString() === match.params.field_id);
  const title = loading ? res?.field_name || match.params.field_id : undefined;

  useDocumentTitle(title, 'Field');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <Icon name={res.datatype_category} tooltip={res.datatype_fullname} />
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

/**
 * Renders a dataset breadcrumb's inline content: an HTTP method pill for API
 * endpoints (matching the Dataset Group table and Dataset header) or the
 * dataset-type icon otherwise, followed by the dataset name. Shared by the
 * current-dataset crumb and the injected parent-dataset crumbs.
 *
 * Gated on dataset_subtype_operation, which only the subtypes of an API endpoint have. A subtype
 * that has none says nothing the type icon does not — a Power BI item's subtype is its kind, which
 * the icon already shows — so those keep the icon. It is also the property that colours the pill,
 * and the one dataset_ancestors carries, so a parent-endpoint crumb draws the same as its own page.
 * @param {{ dataset: object, name?: string }} props - The dataset row and an
 *   optional display name (defaults to `dataset.dataset_name`).
 */
export const DatasetCrumbContent = ({ dataset, name }) => (
  <span className="breadcrumb-item-content">
    {dataset.parent_dataset_id === null && dataset.dataset_subtype_operation ? (
      <Pill
        label={dataset.dataset_subtype_name}
        className={DatasetSubtypeOperation[dataset.dataset_subtype_operation]?.className}
      />
    ) : (
      <Icon name={dataset.dataset_type_name} tooltip={dataset.dataset_type_name} />
    )}
    <span className="breadcrumb-item-text">{name ?? dataset.dataset_name}</span>
  </span>
);

const CurrentDataset = ({ match }) => {
  const res = subjects.currentDataset.value;

  const loading =
    res === 'not_found' ||
    (res?.dataset_id && res.dataset_id.toString() === match.params.dataset_id);
  const title = loading ? res?.dataset_name || match.params.dataset_id : undefined;

  useDocumentTitle(title, 'Dataset');

  if (loading) {
    return <DatasetCrumbContent dataset={res} name={title} />;
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentDatasetGroup = ({ match }) => {
  const res = subjects.currentDatasetGroup.value;

  const loading =
    res === 'not_found' ||
    (res?.dataset_group_id && res.dataset_group_id.toString() === match.params.dataset_group_id);
  const title = loading ? res?.dataset_group_name || match.params.dataset_group_id : undefined;

  useDocumentTitle(title, 'Dataset Group');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentCustomProperty = ({ match }) => {
  const res = subjects.currentCustomProperty.value;

  const loading =
    res === 'not_found' ||
    (res?.custom_property_id &&
      res.custom_property_id.toString() === match.params.custom_property_id);
  const title = loading ? res?.custom_property_name || match.params.custom_property_id : undefined;

  useDocumentTitle(title, 'Custom Property');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentDatasource = ({ match }) => {
  const res = subjects.currentDatasource.value;

  const loading =
    res === 'not_found' ||
    (res?.datasource_id && res.datasource_id.toString() === match.params.datasource_id);
  const title = loading ? res?.datasource_name || match.params.datasource_id : undefined;

  useDocumentTitle(title, 'Datasource');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <Icon name={res.datasource_type_category} tooltip={res.datasource_type_name} />
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentSystem = ({ match }) => {
  const res = subjects.currentSystem.value;

  const loading =
    res === 'not_found' || (res?.system_id && res.system_id.toString() === match.params.system_id);
  const title = loading ? res?.system_name || match.params.system_id : undefined;

  useDocumentTitle(title, 'System');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentAttribute = ({ match }) => {
  const res = subjects.currentAttribute.value;

  const loading =
    res === 'not_found' ||
    (res?.attribute_id && res.attribute_id.toString() === match.params.attribute_id);
  const title = loading ? res?.attribute_name || match.params.attribute_id : undefined;

  useDocumentTitle(title, 'Attribute');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <Icon name={res.attribute_role_name} tooltip={res.attribute_role_name} />
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentAttributeValue = ({ match }) => {
  const res = subjects.currentAttributeValue.value;
  const loading =
    res === 'not_found' ||
    (res?.attribute_value_id &&
      res.attribute_value_id.toString() === match.params.attribute_value_id);

  const title = loading ? res?.attribute_value_name : undefined;

  useDocumentTitle(title, 'Attribute Value');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <Icon name={'attribute-value'} tooltip={'Attribute Value'} />
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

/**
 * Renders an entity breadcrumb's inline content (the entity name). Shared by the
 * current-entity crumb and the injected parent-entity crumbs so a nested entity's trail reflects
 * its actual `parent_entity_id` hierarchy.
 * @param {{ name: string }} props - The display name.
 */
export const EntityCrumbContent = ({ name }) => (
  <span className="breadcrumb-item-content">
    <Icon name={'entity'} tooltip={'Entity'} />
    <span className="breadcrumb-item-text">{name}</span>
  </span>
);

const CurrentEntity = ({ match }) => {
  const res = subjects.currentEntity.value;

  const loading =
    res === 'not_found' || (res?.entity_id && res.entity_id.toString() === match.params.entity_id);
  const title = loading ? res?.entity_name || match.params.entity_id : undefined;

  useDocumentTitle(title, 'Entity');

  if (loading) {
    return <EntityCrumbContent name={title} />;
  }
  return <SkeletonLoader width="7.5vw" />;
};

/**
 * A glossary crumb. Exported so a crumb built outside the route match can render identically — see
 * padBreadcrumbsWithGlossaryTrail in components/Breadcrumbs, which puts an attribute's glossary in the
 * trail even though the attribute route has no glossary_id segment.
 * @param {{ name: string }} props
 */
export const GlossaryCrumbContent = ({ name }) => (
  <span className="breadcrumb-item-content">
    <span className="breadcrumb-item-text">{name}</span>
  </span>
);

const CurrentGlossary = ({ match }) => {
  const res = subjects.currentGlossary.value;

  const loading =
    res === 'not_found' ||
    (res?.glossary_id && res.glossary_id.toString() === match.params.glossary_id);
  const title = loading ? res?.glossary_name || match.params.glossary_id : undefined;

  useDocumentTitle(title, 'Glossary');

  if (loading) {
    return <GlossaryCrumbContent name={title} />;
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentTask = ({ match }) => {
  const res = subjects.currentTask.value;

  const loading =
    res === 'not_found' || (res?.task_id && res.task_id.toString() === match.params.task_id);
  const title = loading ? res?.task_name || match.params.task_id : undefined;

  useDocumentTitle(title, 'Task');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentJob = ({ match }) => {
  const res = subjects.currentJob.value;

  const loading =
    res === 'not_found' || (res?.job_id && res.job_id.toString() === match.params.job_id);
  const titleRaw = loading
    ? new Date(res?.job_started_timestamp).toLocaleString('sv-SE') || match.params.job_id
    : undefined;
  const title = loading
    ? formatData(res?.job_started_timestamp, 'datetime') || match.params.job_id
    : undefined;

  useDocumentTitle(titleRaw, 'Job');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentUser = ({ match }) => {
  const res = subjects.currentUser.value;

  const loading =
    res === 'not_found' || (res?.user_id && res.user_id.toString() === match.params.user_id);
  const title = loading ? res?.user_fullname || match.params.user_id : undefined;

  useDocumentTitle(title, 'User');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentUserGroup = ({ match }) => {
  const res = subjects.currentUserGroup.value;

  const loading =
    res === 'not_found' ||
    (res?.user_group_id && res.user_group_id.toString() === match.params.user_group_id);
  const title = loading ? res?.user_group_name || match.params.user_group_id : undefined;

  useDocumentTitle(title, 'User Group');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentConnection = ({ match }) => {
  const res = subjects.currentConnection.value;

  const loading =
    res === 'not_found' ||
    (res?.connection_id && res.connection_id.toString() === match.params.connection_id);
  const title = loading ? res?.connection_name || match.params.connection_id : undefined;

  useDocumentTitle(title, 'Connection');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

const CurrentSchedule = ({ match }) => {
  const res = subjects.currentSchedule.value;

  const loading =
    res === 'not_found' ||
    (res?.schedule_id && res.schedule_id.toString() === match.params.schedule_id);
  const title = loading ? res?.schedule_name || match.params.schedule_id : undefined;

  useDocumentTitle(title, 'Schedule');

  if (loading) {
    return (
      <span className="breadcrumb-item-content">
        <span className="breadcrumb-item-text">{title}</span>
      </span>
    );
  }
  return <SkeletonLoader width="7.5vw" />;
};

/**
 * Sends /attributes to the tab that now holds the attribute list, so the short URL keeps working.
 * routesConfig elements are component references, not elements — PrivateRoute renders <Component />.
 */
const AttributesRedirect = () => <Navigate to={`/glossaries?tab=${Asset.Attribute}`} replace />;

export const routesConfig = [
  {
    path: '/systems',
    breadcrumb: 'Systems',
    element: Systems,
    title: 'Systems',
  },
  {
    path: '/systems/:system_id',
    breadcrumb: CurrentSystem,
    //tooltip: "System: " + systemObservable.currentSystemValue.system_name,
    element: System,
  },
  {
    path: '/systems/:system_id/:datasource_id',
    breadcrumb: CurrentDatasource,
    //tooltip: "Datasource: " + datasourceObservable.currentDatasourceValue.datasource_name,
    element: Datasource,
  },
  {
    path: '/systems/:system_id/:datasource_id/:dataset_group_id',
    breadcrumb: CurrentDatasetGroup,
    //tooltip: "Dataset Group: " + datasetGroupObservable.currentDatasetGroupValue.dataset_group_name,
    element: DatasetGroup,
  },
  {
    path: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id',
    breadcrumb: CurrentDataset,
    //tooltip: "Dataset: " + datasetObservable.currentDatasetValue.dataset_name,
    element: Dataset,
  },
  {
    path: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
    breadcrumb: CurrentField,
    //tooltip: "Field: " + fieldObservable.currentFieldValue.field_name,
    element: Field,
  },
  {
    path: '/attributes/:attribute_id',
    breadcrumb: CurrentAttribute,
    element: Attribute,
  },
  {
    // The attribute list lives as a tab of the Glossaries page. This path is kept so existing links
    // still work, and because the attribute and attribute-value breadcrumbs are resolved from it —
    // see padBreadcrumbsWithGlossaryTrail in components/Breadcrumbs, which replaces this crumb with
    // the glossary the attribute belongs to.
    path: '/attributes',
    breadcrumb: 'All Attributes',
    element: AttributesRedirect,
    title: 'Attributes',
  },
  {
    path: '/attributes/:attribute_id/:attribute_value_id',
    breadcrumb: CurrentAttributeValue,
    element: AttributeValue,
    title: 'Attribute Value',
  },
  {
    path: '/glossaries',
    breadcrumb: 'Glossaries',
    element: Glossaries,
    title: 'Glossaries',
  },
  {
    path: '/glossaries/:glossary_id',
    breadcrumb: CurrentGlossary,
    //tooltip: "Glossary: " + glossaryObservable.currentGlossaryValue.glossary_name,
    element: Glossary,
  },
  {
    path: '/glossaries/:glossary_id/:entity_id',
    breadcrumb: CurrentEntity,
    element: Entity,
  },
  {
    path: '/connections/:connection_id',
    breadcrumb: CurrentConnection,
    element: Connection,
    roles: [Role.admin],
  },
  {
    path: '/connections',
    breadcrumb: 'Connections',
    element: Connections,
    roles: [Role.admin],
    title: 'Connections',
  },
  {
    path: '/profile',
    breadcrumb: 'Profile',
    element: Profile,
    title: 'Profile',
  },
  {
    path: '/tasks/:task_id/:job_id',
    breadcrumb: CurrentJob,
    element: Job,
  },
  {
    path: '/tasks/:task_id',
    breadcrumb: CurrentTask,
    element: Task,
    //roles: [Role.admin],
  },
  {
    path: '/schedules/:schedule_id',
    breadcrumb: CurrentSchedule,
    element: Schedule,
    roles: [Role.admin],
  },
  {
    path: '/schedules',
    breadcrumb: 'Schedules',
    element: Schedules,
    roles: [Role.admin],
    title: 'Schedules',
  },
  {
    path: '/tasks',
    breadcrumb: 'Tasks',
    element: Tasks,
    roles: [Role.admin],
    title: 'Tasks',
  },
  {
    path: '/users/:user_id',
    breadcrumb: CurrentUser,
    element: User,
  },
  {
    path: '/users',
    breadcrumb: 'Users',
    element: Users,
    roles: [Role.admin],
    title: 'Users',
  },
  {
    path: '/user_groups/:user_group_id',
    breadcrumb: CurrentUserGroup,
    element: UserGroup,
  },
  {
    path: '/user_groups',
    breadcrumb: 'User Groups',
    element: UserGroups,
    roles: [Role.admin],
    title: 'User Groups',
  },
  {
    path: '/attribute_units',
    breadcrumb: 'Attribute Units',
    element: AttributeUnits,
    roles: [Role.admin],
    title: 'Attribute Units',
  },
  {
    path: '/custom_properties/:custom_property_id',
    breadcrumb: CurrentCustomProperty,
    element: CustomProperty,
  },
  {
    path: '/custom_properties',
    breadcrumb: 'Custom Properties',
    element: CustomProperties,
    title: 'Custom Properties',
  },
  {
    path: '/settings',
    breadcrumb: 'Settings',
    element: Settings,
    title: 'Settings',
  },
  {
    path: '/',
    breadcrumb: 'Home',
    element: Home,
    title: 'Katalogue',
  },
];
